import { formatMsg } from './format.js';
import { isMsg, type Bind, type Locale, type Params } from './types.js';

/**
 * Привязка каталога локали к языку на клиенте: каждый лист `Msg` превращается в
 * замыкание `(params) => formatMsg(...)`, простые строки и массивы обходятся как есть.
 *
 * Зачем это нужно: каталог приезжает клиенту по HTTP как ЧИСТЫЙ JSON, поэтому лист
 * `Msg` — это объект `{ s, p? }`, а не функция. Без привязки компонент был бы обязан
 * сам звать formatMsg и знать про локаль, то есть формат сообщений протёк бы в вёрстку.
 * Тип `Bind<T>` под это заведён в волне 01 (shared/i18n/types.ts) и до сих пор нигде
 * не использовался — это ровно та форма, под которую он писался.
 *
 * Живёт в shared/, а не в web/: обход дерева не использует ничего браузерного, поэтому
 * тестируется обычным node-тестом в СУЩЕСТВУЮЩЕМ vitest-проекте, без jsdom.
 */

function isPlainRecord(node: unknown): node is Record<string, unknown> {
  return typeof node === 'object' && node !== null;
}

function bindNode(node: unknown, locale: Locale): unknown {
  if (typeof node === 'string') return node;

  if (Array.isArray(node)) return node.map((entry) => bindNode(entry, locale));

  if (isMsg(node)) {
    const leaf = node;
    return (params: Params) => formatMsg(leaf, params, locale);
  }

  if (isPlainRecord(node)) {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(node)) out[key] = bindNode(node[key], locale);
    return out;
  }

  // Каталог гарантированно JSON-сериализуем (сервер проверяет это на старте,
  // server/i18n/boot-assert.ts). Сюда можно попасть только если сеть отдала что-то
  // за пределами string/Msg/array/object — например протухший или подменённый ответ.
  // Явная ошибка сразу лучше молча испорченного дерева, всплывающего в вёрстке.
  throw new TypeError(
    `i18n bindCatalog: catalog leaf of type "${typeof node}" is not string/Msg/array/object`,
  );
}

/**
 * ЕДИНСТВЕННЫЙ каст — на последней строке. Обход идёт на уровне значений и раскладывает
 * каждый Msg в замыкание по ходу дела; доказать компилятору, что произвольный `T` после
 * такого обхода стал именно `Bind<T>`, изнутри реализации нельзя — это разрыв между
 * значением и типом, который рекурсивные условные типы не закрывают. Корректность
 * держится на bind.test.ts, а не на сигнатуре.
 */
export function bindCatalog<T>(raw: T, locale: Locale): Bind<T> {
  return bindNode(raw, locale) as Bind<T>;
}
